"use client";

import { c as _c } from "react/compiler-runtime";
import { IconCryptoWallet } from "@central-icons-react/square-outlined-radius-0-stroke-1.5";
import { IconCircleCheckOutline18, IconUserOutline18 } from "nucleo-ui-outline-18";
import UserAvatar from "@/app/(v2)/_components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WalletAddressMeta } from "@/components/ui/WalletAddressMeta";
import type { B3Profile } from "@/hooks/useB3Profile";
import { formatUsername } from "@/lib/utils";
import type { OrganizationWallet } from "@/types/wallet";
import { forwardRef, type ReactNode } from "react";

/** Per-wallet aggregated balance + chain summary, computed once outside the view. */
export interface WalletBalanceSummary {
  /** Pre-formatted USD string (e.g. "$1.20K") or null if loading/no balance. */
  usdValue: string | null;
  /** Top chains by balance, with display info ready to paint. */
  chains: Array<{
    id: number;
    name: string;
    iconUrl?: string | null;
  }>;
  /** Count of additional chains not shown (rendered as "+N"). */
  extraChainsCount: number;
  /** True while the indexer hasn't returned for this wallet yet. */
  isLoadingBalance: boolean;
  /** True while the chain icon/name lookup hasn't returned. */
  isLoadingChains: boolean;
}
const EMPTY_SUMMARY: WalletBalanceSummary = {
  usdValue: null,
  chains: [],
  extraChainsCount: 0,
  isLoadingBalance: false,
  isLoadingChains: false
};

/**
 * Pure presentational variant of `OrganizationWalletOption` from
 * AddressInput. Takes a pre-computed balance/chain summary so the view layer
 * can be rendered in stories without network access. The connected version
 * lives in `AddressInput.tsx` and supplies the same summary via hooks.
 */
export function OrganizationWalletOptionView(t0) {
  const $ = _c(21);
  const {
    wallet,
    summary,
    className,
    nameClassName
  } = t0;
  const s = summary ?? EMPTY_SUMMARY;
  const t1 = nameClassName || "text-ds-text-primary font-medium";
  let t2;
  if ($[0] !== t1 || $[1] !== wallet.name) {
    t2 = <span className={t1}>{wallet.name}</span>;
    $[0] = t1;
    $[1] = wallet.name;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== wallet.isDefault) {
    t3 = wallet.isDefault ? <Badge variant="info">Default</Badge> : null;
    $[3] = wallet.isDefault;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <div className="flex items-center gap-2">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = !s.isLoadingBalance ? s.usdValue : null;
  let t6;
  if ($[8] !== s.chains || $[9] !== s.isLoadingChains) {
    t6 = !s.isLoadingChains ? s.chains : [];
    $[8] = s.chains;
    $[9] = s.isLoadingChains;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== s.extraChainsCount || $[12] !== s.isLoadingBalance || $[13] !== t5 || $[14] !== t6 || $[15] !== wallet.address) {
    t7 = <WalletAddressMeta address={wallet.address} usdValue={t5} chains={t6} extraChainsCount={s.extraChainsCount} isLoadingBalance={s.isLoadingBalance} className="mt-1" />;
    $[11] = s.extraChainsCount;
    $[12] = s.isLoadingBalance;
    $[13] = t5;
    $[14] = t6;
    $[15] = wallet.address;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== className || $[18] !== t4 || $[19] !== t7) {
    t8 = <div className={className}>{t4}{t7}</div>;
    $[17] = className;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}

/**
 * Visual mode of the AddressInput. The outer component decides which one
 * applies based on the live form state and passes it in as a single discriminator,
 * so the view never has to recompute (isValidAddress / isENSName / hasValidSelection).
 */
export type AddressInputMode = /** Org-wallet card (selected value matches a known organization wallet). */
"wallet"
/** ENS-resolved profile card. */ | "profile"
/** Plain valid hex address with no profile / wallet match. */ | "address-only"
/** The default editable input. */ | "input";
export interface AddressInputViewProps {
  /** Field label. Required visually. */
  label: string;
  /** Optional helper text under the label. */
  description?: string;
  /** Slot rendered to the right of the label (e.g. variable picker). */
  headerRight?: ReactNode;
  /** Current input string (raw / unparsed). */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** When true, marks the field with a red asterisk. */
  required?: boolean;
  /** When set, the popover is rendered inside its container — required inside dialogs. */
  popoverModal?: boolean;
  /**
   * Compact layout: collapses the wallet card to a single 42px-tall row, used
   * inside csvTable grid cells where row height must not jitter.
   */
  compact?: boolean;
  /**
   * When false, hides the inline "Wallets" badge button. The connected
   * `AddressInput` already empties `matchingWallets` in this mode, so the
   * autocomplete popover collapses naturally.
   */
  allowWalletPicker?: boolean;
  /** Which presentation variant to render. Computed by the outer component. */
  mode: AddressInputMode;
  /** True when the input itself should be focused / editable. */
  isEditing: boolean;
  /** True if the input is open as a popover (autocomplete). */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEditClick: () => void;
  onFocus: () => void;
  onBlur: () => void;
  onWalletClick: (address: string) => void;
  onProfileClick: () => void;
  onWalletsButtonClick: () => void;
  /** Resolved wallet matching the current value (when mode is "wallet"). */
  selectedWallet: OrganizationWallet | null;
  /** Resolved profile matching the current value (when mode is "profile"). */
  profile: B3Profile | null;
  /** Resolved address (after ENS lookup) — used for avatar / display. */
  resolvedAddress: string | null;
  /** Wallets matching the current input (autocomplete suggestions). */
  matchingWallets: OrganizationWallet[];
  /**
   * Render a wallet row body. The connected outer component wires this to a
   * tiny bridge that calls `useSimBalance` + `useChainInfoMap` per wallet.
   * Stories pass a synchronous renderer using `OrganizationWalletOptionView`.
   */
  renderWalletBody: (wallet: OrganizationWallet, opts: {
    variant: "selected" | "match";
  }) => ReactNode;
  /** Background profile fetch in progress. */
  isLoadingProfile: boolean;
  /** True when the typed value is a valid hex address. */
  isValidAddress: boolean;
  /** True when the typed value looks like an ENS name. */
  isENSName: boolean;
  /** True when the field has been touched and is required + empty. */
  showError: boolean;
  /** True when the address itself is invalid. */
  showAddressError: boolean;
  /** When true, fully disable the field (e.g. read-only modes). */
  disabled?: boolean;
}
export const AddressInputView = forwardRef<HTMLInputElement, AddressInputViewProps>(function AddressInputView(t0, inputRef) {
  const $ = _c(53);
  const {
    label,
    description,
    headerRight,
    value,
    onChange,
    placeholder: t1,
    required: t2,
    popoverModal: t3,
    compact: t4,
    allowWalletPicker: t5,
    mode,
    isEditing,
    open,
    onOpenChange,
    onEditClick,
    onFocus,
    onBlur,
    onWalletClick,
    onProfileClick,
    onWalletsButtonClick,
    selectedWallet,
    profile,
    resolvedAddress,
    matchingWallets,
    renderWalletBody,
    isLoadingProfile,
    isValidAddress,
    isENSName,
    showError,
    showAddressError,
    disabled: t6
  } = t0;
  const placeholder = t1 === undefined ? "Enter a blockchain address" : t1;
  const required = t2 === undefined ? false : t2;
  const popoverModal = t3 === undefined ? false : t3;
  const compact = t4 === undefined ? false : t4;
  const allowWalletPicker = t5 === undefined ? true : t5;
  const disabled = t6 === undefined ? false : t6;
  const safeValue = typeof value === "string" ? value : value != null ? String(value) : "";
  let t7;
  if ($[0] !== headerRight || $[1] !== label || $[2] !== required) {
    t7 = label && <div className="flex items-center justify-between gap-3"><label className="field-label block">{label}{required && <span className="ml-1 text-red-500">*</span>}</label>{headerRight}</div>;
    $[0] = headerRight;
    $[1] = label;
    $[2] = required;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  let t8;
  if ($[4] !== description) {
    t8 = description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>;
    $[4] = description;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  let t9;
  if ($[6] !== allowWalletPicker || $[7] !== compact || $[8] !== disabled || $[9] !== inputRef || $[10] !== isEditing || $[11] !== isLoadingProfile || $[12] !== isValidAddress || $[13] !== matchingWallets || $[14] !== mode || $[15] !== onBlur || $[16] !== onChange || $[17] !== onEditClick || $[18] !== onFocus || $[19] !== onOpenChange || $[20] !== onProfileClick || $[21] !== onWalletClick || $[22] !== onWalletsButtonClick || $[23] !== open || $[24] !== placeholder || $[25] !== popoverModal || $[26] !== profile || $[27] !== renderWalletBody || $[28] !== resolvedAddress || $[29] !== safeValue || $[30] !== selectedWallet || $[31] !== showAddressError || $[32] !== showError) {
    t9 = mode === "wallet" && selectedWallet ? <div className={compact ? "dark:hover:bg-ds-neutral-700 bg-ds-surface-card flex min-h-[42px] w-full cursor-pointer items-center gap-2 rounded-lg border border-green-500 px-3 py-2 text-sm transition-colors hover:bg-emerald-50/40" : "dark:hover:bg-ds-neutral-700 bg-ds-surface-card flex min-h-[56px] w-full cursor-pointer items-center gap-3 rounded-xl border border-green-500 px-3 py-2.5 text-sm transition-colors hover:bg-emerald-50/40"} onClick={onEditClick}>{compact ? <><IconCryptoWallet className="text-ds-text-subtle h-4 w-4 flex-shrink-0" /><span className="text-ds-text-primary truncate font-medium">{selectedWallet.name}</span><span className="label-xs text-ds-text-subtle truncate font-mono">{selectedWallet.address.slice(0, 6)}...{selectedWallet.address.slice(-4)}</span><div className="flex-1" /><IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" /></> : <><div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300"><IconCryptoWallet className="h-4 w-4" /></div>{renderWalletBody(selectedWallet, {
          variant: "selected"
        })}<IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" /></>}</div> : mode === "profile" && profile ? <div className="dark:hover:bg-ds-neutral-700 bg-ds-surface-card hover:bg-ds-surface-hover flex min-h-[42px] w-full cursor-pointer items-center gap-2 rounded-lg border border-green-500 px-3 py-2 text-sm transition-colors" onClick={onEditClick}><UserAvatar address={resolvedAddress || ""} size={24} includeAvatar={true} includeUsername={false} /><div className="min-w-0 flex-1"><div className="flex items-center gap-2">{(profile.displayName || profile.name) && <span className="text-ds-text-primary font-medium">{formatUsername(profile.displayName || profile.name)}</span>}<span className="label-xs truncate font-mono">{resolvedAddress ? `${resolvedAddress.slice(0, 6)}...${resolvedAddress.slice(-4)}` : ""}</span></div></div><IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" /></div> : mode === "address-only" ? <div className="dark:hover:bg-ds-neutral-700 bg-ds-surface-card hover:bg-ds-surface-hover flex min-h-[42px] w-full cursor-pointer items-center gap-2 rounded-lg border border-green-500 px-3 py-2 text-sm transition-colors" onClick={onEditClick}><UserAvatar address={safeValue} size={24} includeAvatar={true} includeUsername={false} /><span className="text-ds-text-primary truncate font-mono text-sm">{safeValue.slice(0, 6)}...{safeValue.slice(-4)}</span><div className="flex-1" /><IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" /></div> : <Popover open={open} onOpenChange={onOpenChange} modal={popoverModal}><PopoverTrigger asChild={true}><div className="relative"><Input type="text" value={safeValue || ""} onChange={e => onChange(e.target.value)} onFocus={onFocus} onBlur={onBlur} placeholder={placeholder} ref={inputRef} disabled={disabled} className={`${allowWalletPicker ? "pr-36" : "pr-10"} font-mono ${showError || showAddressError ? "border-red-500 focus-visible:ring-red-500" : isValidAddress ? "border-green-500 focus-visible:ring-green-500" : ""}`} autoFocus={isEditing} />{allowWalletPicker && <button type="button" onMouseDown={_temp} onClick={onWalletsButtonClick} className="absolute inset-y-0 right-2 flex items-center" disabled={disabled}><Badge variant="outline" className="micro-label dark:bg-ds-neutral-800 gap-1.5 bg-zinc-50 px-2.5 py-0.5 transition-colors hover:border-blue-200 hover:text-blue-600 dark:hover:border-blue-800 dark:hover:text-blue-400">Wallets <IconCryptoWallet className="h-3 w-3" /></Badge></button>}<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">{isLoadingProfile && <PixelLoader size="sm" color="blue" />}{!isLoadingProfile && isValidAddress && <IconCircleCheckOutline18 className="h-4 w-4 text-green-500" />}</div></div></PopoverTrigger>{(matchingWallets.length > 0 || profile && profile.address) && <PopoverContent className="border-ds-border-default bg-ds-surface-card w-[var(--radix-popover-trigger-width)] p-0 shadow-xl" align="start" onOpenAutoFocus={_temp2}><div className="max-h-[200px] overflow-y-auto">{matchingWallets.length > 0 && <div><div className="dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2"><IconUserOutline18 className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" /><p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">Org Wallets</p></div>{matchingWallets.map(wallet => <button key={wallet.id} onClick={() => onWalletClick(wallet.address)} className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20"><div className="border-ds-border-default text-ds-text-subtle dark:bg-ds-neutral-800 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border bg-zinc-50"><IconCryptoWallet className="h-4 w-4" /></div>{renderWalletBody(wallet, {
                variant: "match"
              })}<IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500 opacity-0" /></button>)}</div>}{profile && profile.address && <div><div className={`dark:bg-ds-neutral-900 flex items-center gap-1.5 bg-zinc-50 px-3 py-2 ${matchingWallets.length > 0 ? "border-ds-border-default border-t" : ""}`}><IconUserOutline18 className="text-ds-text-muted dark:text-ds-text-subtle h-3 w-3" /><p className="text-ds-text-muted dark:text-ds-text-subtle text-xs font-semibold uppercase tracking-wide">Resolved Address</p></div><button onClick={onProfileClick} className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/20"><UserAvatar address={profile.address} size={32} includeAvatar={true} includeUsername={false} /><div className="min-w-0 flex-1">{profile.name && <p className="body-sm-medium truncate">{formatUsername(profile.displayName || profile.name)}</p>}<p className="text-ds-text-subtle dark:text-ds-text-disabled truncate font-mono text-xs">{profile.address.slice(0, 10)}...{profile.address.slice(-8)}</p></div><IconCircleCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-500" /></button></div>}</div></PopoverContent>}</Popover>;
    $[6] = allowWalletPicker;
    $[7] = compact;
    $[8] = disabled;
    $[9] = inputRef;
    $[10] = isEditing;
    $[11] = isLoadingProfile;
    $[12] = isValidAddress;
    $[13] = matchingWallets;
    $[14] = mode;
    $[15] = onBlur;
    $[16] = onChange;
    $[17] = onEditClick;
    $[18] = onFocus;
    $[19] = onOpenChange;
    $[20] = onProfileClick;
    $[21] = onWalletClick;
    $[22] = onWalletsButtonClick;
    $[23] = open;
    $[24] = placeholder;
    $[25] = popoverModal;
    $[26] = profile;
    $[27] = renderWalletBody;
    $[28] = resolvedAddress;
    $[29] = safeValue;
    $[30] = selectedWallet;
    $[31] = showAddressError;
    $[32] = showError;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== isENSName || $[35] !== isEditing || $[36] !== profile) {
    t10 = isENSName && profile?.address && !isEditing && <p className="text-ds-text-subtle dark:text-ds-text-disabled font-mono text-xs">Resolves to: {profile.address.slice(0, 10)}...{profile.address.slice(-8)}</p>;
    $[34] = isENSName;
    $[35] = isEditing;
    $[36] = profile;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== showError) {
    t11 = showError && <p className="micro-label text-red-600 dark:text-red-400">Address is required</p>;
    $[38] = showError;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== isEditing || $[41] !== showAddressError) {
    t12 = showAddressError && isEditing && <p className="micro-label text-red-600 dark:text-red-400">Please enter a valid address</p>;
    $[40] = isEditing;
    $[41] = showAddressError;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== value) {
    t13 = !value && <p className="meta-xs">Enter a blockchain address</p>;
    $[43] = value;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  let t14;
  if ($[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13 || $[49] !== t7 || $[50] !== t8 || $[51] !== t9) {
    t14 = <div className="space-y-2">{t7}{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = t14;
  } else {
    t14 = $[52];
  }
  return t14;
});
export default AddressInputView;
function _temp(e_0) {
  return e_0.preventDefault();
}
function _temp2(e_1) {
  return e_1.preventDefault();
}